import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Button } from '@/components/ui-kit/button';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteSkillCategory, useGetSkillCategories } from '../../hooks/use-skill-categories';
import { DEFAULT_SKILL_CATEGORIES_ORDER } from '../../graphql/queries';
import { SkillCategoriesPagingResult, SkillCategory } from '../../types/skill-category.types';
import { SkillCategoryFormDialog } from '../skill-category-form-dialog/skill-category-form-dialog';
import { createSkillCategoryTableColumns } from '../skill-category-table-columns/skill-category-table-columns';

/** "Skill Categories" tab of the Skill Manager: the person-level skill categories (Business, Tech, ...). */
export const SkillCategoryTab = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SETTINGS.SKILL_CATEGORIES_CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SETTINGS.SKILL_CATEGORIES_UPDATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.SETTINGS.SKILL_CATEGORIES_DELETE);

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_SKILL_CATEGORIES_ORDER,
  });
  const [formState, setFormState] = useState<{
    open: boolean;
    skillCategory: SkillCategory | null;
  }>({ open: false, skillCategory: null });
  const [deleteState, setDeleteState] = useState<{
    open: boolean;
    skillCategory: SkillCategory | null;
  }>({ open: false, skillCategory: null });

  const { data, isLoading, error } = useGetSkillCategories({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { SkillCategorys?: SkillCategoriesPagingResult })?.SkillCategorys;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteSkillCategory, isPending: isDeleting } = useDeleteSkillCategory();

  const handleDeleteConfirm = async () => {
    if (!deleteState.skillCategory) return;
    await deleteSkillCategory(deleteState.skillCategory.ItemId);
    setDeleteState({ open: false, skillCategory: null });
  };

  const columns = createSkillCategoryTableColumns({
    t,
    onEdit: (skillCategory) => setFormState({ open: true, skillCategory }),
    onDelete: (skillCategory) => setDeleteState({ open: true, skillCategory }),
    canUpdate,
    canDelete,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        {canCreate && (
          <Button
            className="w-full sm:w-auto"
            onClick={() => setFormState({ open: true, skillCategory: null })}
          >
            <Plus className="size-4" />
            {t('CREATE_SKILL_CATEGORY')}
          </Button>
        )}
      </div>

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <SkillCategoryFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, skillCategory: open ? prev.skillCategory : null }))
        }
        skillCategory={formState.skillCategory}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, skillCategory: open ? deleteState.skillCategory : null })
        }
        title={t('DELETE_SKILL_CATEGORY')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
